import type { ProductVariant } from '../../../redux/productSlice';
import type { ProductContextType } from '../../../Context/ProductContext';

/**
 * Shared typed form models for canonical product creation.
 * Extracted from NewProduct.tsx without behavior change so the owner-setup
 * wizard and the normal product screen share one implementation.
 */

export type ProductFormData = ProductContextType['formData'];

export type ProductPriceType = 'item' | 'unit';

export type CreateProductPresentation = 'simplified' | 'full';

export type CreateProductSource =
  | 'default'
  | 'owner_setup'
  | 'activation_tracker'
  | 'deeplink';

export interface ProductFormErrors {
  productname?: string;
  category?: string;
  unitofmass?: string;
  buyingprice?: string;
  sellingprice?: string;
  quantity?: string;
  mass?: string;
  size?: string;
  model?: string;
  msl?: string;
  barcode?: string;
}

export type EditableVariant = Omit<
  ProductVariant,
  | 'bp_variant_buying_price'
  | 'bp_variant_selling_price'
  | 'bp_variant_quantity'
  | 'bp_variant_minimum_stock_level'
> & {
  _localId?: string;
  _softDeleted?: boolean;
  bp_variant_buying_price: number | string;
  bp_variant_selling_price: number | string;
  bp_variant_quantity: number | string;
  bp_variant_minimum_stock_level: number | string;
};

export const EMPTY_PRODUCT_FORM: ProductFormData = {
  productname: '',
  category: '',
  subcategory: '',
  unitofmass: '',
  buyingprice: '',
  sellingprice: '',
  quantity: '',
  pid: '',
  mass: '',
  size: '',
  model: '',
  msl: '',
  wastedquantity: '',
  barcode: '',
  bp_product_image: '',
  bp_product_image_uri: '',
  hasVariants: false,
  variantAttributes: [],
  variants: [],
};

export const UNIT_OF_MASS_OPTIONS = [
  { value: '', label: 'Select unit of mass' },
  { value: 'Mg', label: 'Milligrams(Mg)' },
  { value: 'g', label: 'Grams(g)' },
  { value: 'Kg', label: 'Kilograms(Kg)' },
  { value: 'Ml', label: 'Millilitres(Ml)' },
  { value: 'Lt', label: 'Litres(Lt)' },
  { value: 'inch', label: 'Inch(inch)' },
  { value: 'Ft', label: 'Feet(Ft)' },
  { value: 'Mm', label: 'Millimeters(Mm)' },
  { value: 'Cm', label: 'Centimetres(Cm)' },
  { value: 'M', label: 'Metres(M)' },
];

export const SIZE_OPTIONS = [
  { value: '', label: 'Select size' },
  { value: 'xsmall', label: 'Extra Small' },
  { value: 'small', label: 'Small' },
  { value: 'medium', label: 'Medium' },
  { value: 'large', label: 'Large' },
  { value: 'xlarge', label: 'Extra Large' },
  { value: '2xlarge', label: 'Extra Extra Large' },
  { value: '3xlarge', label: 'Three Extra Extra Large' },
  { value: '4xlarge', label: 'Four Extra Extra Large' },
];

/** Always-available attribute name chips for variant product creation (no values). */
export const SUGGESTED_VARIANT_ATTRIBUTES = ['Brand', 'Size', 'Color'] as const;

type AttributeChipOption = {
  _id: string;
  bp_attribute_name: string;
  bp_attribute_values: string[];
};

/** Suggested chips first; prefer catalog entry when name already exists. */
export const mergeSuggestedVariantAttributes = <T extends AttributeChipOption>(
  catalogAttributes: T[],
): AttributeChipOption[] => {
  const byName = new Map<string, T>();
  catalogAttributes.forEach(attribute => {
    const key = (attribute.bp_attribute_name || '').trim().toLowerCase();
    if (key) byName.set(key, attribute);
  });

  const suggested = SUGGESTED_VARIANT_ATTRIBUTES.map(name => {
    const existing = byName.get(name.toLowerCase());
    if (existing) {
      byName.delete(name.toLowerCase());
      return existing;
    }
    return {
      _id: `suggested-${name.toLowerCase()}`,
      bp_attribute_name: name,
      bp_attribute_values: [] as string[],
    };
  });

  return [...suggested, ...Array.from(byName.values())];
};

export const PARENT_VARIANT_THUMBNAIL_CAMERA_ID =
  '__parent-variant-thumbnail-camera__';

export const buildLocalVariantId = () =>
  `variant-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

export const getVariantRowId = (
  variant: EditableVariant,
  indexFallback?: number,
): string =>
  String(
    variant._id ||
      variant._localId ||
      (typeof indexFallback === 'number' ? `variant-${indexFallback}` : ''),
  );

export const parseVariantAttributeNames = (value: string): string[] =>
  Array.from(
    new Set(
      String(value || '')
        .split(',')
        .map(name => name.trim())
        .filter(Boolean),
    ),
  );

export const syncVariantAttributes = (
  variants: EditableVariant[],
  attributeNames: string[],
): EditableVariant[] =>
  variants.map(variant => {
    const currentAttributes = variant.bp_variant_attributes || {};
    const nextAttributes: Record<string, string> = {};
    attributeNames.forEach(attributeName => {
      nextAttributes[attributeName] = String(
        currentAttributes[attributeName] || '',
      );
    });
    return {
      ...variant,
      bp_variant_attributes: nextAttributes,
    };
  });

export const createEmptyVariantRow = (
  attributeNames: string[],
): EditableVariant => ({
  _id: '',
  _localId: buildLocalVariantId(),
  _softDeleted: false,
  bp_variant_sku: '',
  bp_variant_attributes: Object.fromEntries(
    attributeNames.map(attributeName => [attributeName, '']),
  ),
  bp_variant_barcode: '',
  bp_variant_buying_price: '',
  bp_variant_selling_price: '',
  bp_variant_quantity: '',
  bp_variant_image: '',
  bp_variant_minimum_stock_level: '',
  bp_variant_display_name: '',
});

/** Result contract shared by the wizard and the normal product screen. */
export type CreateProductResult =
  | {
      kind: 'created';
      savedProductIds: string[];
      refreshStatus: 'succeeded' | 'failed';
      message: string;
    }
  | {
      kind: 'duplicate_or_unsaved';
      unsavedProducts: unknown[];
      message: string;
    }
  | {
      kind: 'failed';
      category:
        | 'offline'
        | 'validation'
        | 'authentication'
        | 'location'
        | 'server'
        | 'unknown';
      userMessage: string;
    };

export type CreateProductSuccessContext = {
  source: CreateProductSource;
  savedProductIds: string[];
  locationId: string;
  locationType: 'real';
  refreshStatus: 'succeeded' | 'failed';
};
